
import React from 'react';
import { Service } from './types';
import { Icon } from './components/Icon';
import { APP_SETTINGS } from './constants';

interface ServicesPageProps {
  services: Service[];
  onBack: () => void;
  onNavigateToPortfolio: (type: 'web' | 'app' | 'multimedia' | 'kaos') => void;
}

const ServicesPage: React.FC<ServicesPageProps> = ({ services, onBack, onNavigateToPortfolio }) => {
  return (
    <div className="min-h-screen bg-[#030712] pt-32 pb-20 text-white">
      <div className="container mx-auto px-6">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group"
        >
          <div className="p-2 glass-morphism rounded-lg group-hover:bg-white/10">
            <Icon name="ArrowRight" className="rotate-180" size={18} />
          </div>
          <span>Kembali ke Beranda</span>
        </button>

        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Semua <span className="gradient-text">Layanan</span> Kami</h1>
          <p className="text-slate-400 max-w-2xl">Jelajahi berbagai solusi digital dan bertenaga AI yang dirancang untuk mempercepat produktivitas dan kreativitas Anda.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const isApparel = service.id === 'kaos-custom' || service.category === 'Apparel' || service.title.toLowerCase().includes('kaos');
            const isWeb = service.id === 'web-dev' || service.title.toLowerCase().includes('website');
            const isApp = service.id === 'app-dev' || service.title.toLowerCase().includes('aplikasi') || service.title.toLowerCase().includes('app');
            const isMultimedia = service.id === 'multimedia-interaktif' || service.title.toLowerCase().includes('multimedia');

            return (
              <div 
                key={service.id} 
                className="group relative p-8 glass-morphism rounded-3xl border border-white/10 hover:border-purple-500/50 transition-all duration-500 flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="p-4 bg-purple-600/10 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <Icon name={service.icon} className="text-purple-400" size={32} />
                  </div>
                  {service.priceRange && (
                    <div className="px-3 py-1 bg-white/5 border border-white/10 rounded-full">
                      <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">{service.priceRange}</span>
                    </div>
                  )}
                </div>
                
                <h3 className="text-2xl font-bold mb-4 group-hover:text-purple-400 transition-colors">{service.title}</h3>
                <p className="text-slate-400 mb-6 leading-relaxed flex-grow">
                  {service.description}
                </p>
                
                {(isWeb || isApp || isMultimedia || isApparel) && (
                  <button 
                    onClick={() => {
                      if(isWeb) onNavigateToPortfolio('web');
                      else if(isApp) onNavigateToPortfolio('app');
                      else if(isMultimedia) onNavigateToPortfolio('multimedia');
                      else if(isApparel) onNavigateToPortfolio('kaos');
                    }}
                    className="w-full mb-6 py-2.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-xs font-bold hover:bg-blue-500 hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                     {isApparel ? (
                        <><Icon name="Shirt" size={14} /> Lihat Detail Produk</>
                     ) : (
                        <><Icon name={isMultimedia ? 'Gamepad' : 'Monitor'} size={14} /> Lihat Portofolio {isWeb ? 'Website' : isApp ? 'Aplikasi' : 'Multimedia'}</>
                     )}
                  </button>
                )}

                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-slate-300">
                      <Icon name="CheckCircle2" className="text-green-500" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <a 
                  href={`mailto:${APP_SETTINGS.contactEmail}`}
                  className="mt-auto flex items-center gap-2 text-sm font-bold text-white group/btn"
                >
                  Konsultasi Layanan <Icon name="ChevronRight" className="group-hover/btn:translate-x-1 transition-transform" size={16} />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
